import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Field, Status } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { CatalogHeader, errorText } from "./common";

export function ProductPage() {
  const { id = "" } = useParams();
  const api = useApi();
  const data = useLoad((a) => a.admin.catalog.item(id), [id]);
  const [notes, setNotes] = useState("");
  const [ndcNoneReason, setNdcNoneReason] = useState("");
  const [msg, setMsg] = useState<{ kind: "info" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setNotes(data.data?.notes ?? "");
    setNdcNoneReason(data.data?.ndcNoneReason ?? "");
  }, [data.data]);
  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.admin.catalog.updateItem(id, { notes: notes || null, ndcNoneReason: ndcNoneReason || null });
      setMsg({ kind: "info", text: "Saved." });
      data.reload();
    } catch (err) {
      setMsg({ kind: "error", text: errorText(err) });
    } finally {
      setBusy(false);
    }
  }
  const p = data.data;
  return (
    <>
      <CatalogHeader title={p?.manufacturerProductName ?? "Product"} intro="Product details as imported from Knowledge. Changes are audited." />
      {data.state === "error" && <Status kind="error" label={`Couldn't load the product (${errorText(data.error)})`} />}
      {msg && <div className="mb-6"><Status kind={msg.kind} label={msg.text} /></div>}
      {p && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card as="section">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-small">
              {([
                ["Type", p.itemType],
                ["Labeler / supplier", `${p.labeler ?? ""} / ${p.supplier ?? ""}`],
                ["Item numbers", `${p.manufacturerItemNumber ?? ""} / ${p.supplierItemNumber ?? ""}`],
                ["Potency", `${p.potencyValue ?? ""} ${p.potencyUnit ?? ""} (${p.potencyLabelText ?? ""})`],
                ["Diluent", p.diluentBase ?? ""],
                ["Package", p.packageVolumeMl ? `${p.packageVolumeMl} mL` : ""],
                ["NDC-11 / NDC-10", `${p.ndc11 ?? "none"} / ${p.ndc10 ?? "none"}`],
                ["NDC status", p.ndcStatus ?? ""],
                ["Procurement", p.procurementStatus],
                ["Knowledge product id", String(p.legacyProductId ?? "")],
              ] as const).map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-ink-secondary">{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
            <h2 className="mb-2 mt-6 text-h3 font-semibold text-anchor">Supplies</h2>
            <ul className="list-disc pl-5">
              {p.antigens.map((a) => (
                <li key={a.id}><Link to={`/catalog/antigens/${a.id}`} className="text-anchor underline">{a.displayName}</Link> ({a.abbreviation})</li>
              ))}
            </ul>
          </Card>
          <Card as="section">
            <form onSubmit={(e) => void save(e)} className="grid gap-4">
              <Field label="Reason there is no NDC" value={ndcNoneReason} onChange={(e) => setNdcNoneReason(e.target.value)} hint="e.g. checked the labeler's NDC list on a date" wide />
              <Field label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} wide />
              <div><Button type="submit" disabled={busy}>Save</Button></div>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}
